/**
 * useSSE — real-time подписка на обновления вкладки через Server-Sent Events.
 *
 * Поток:
 *   1) POST /api/deals/:dealId/stream-ticket (Bearer) → { ticket, expiresIn } — одноразовый тикет;
 *   2) EventSource на GET {API_BASE}/api/deals/:dealId/stream?ticket=<t>
 *      (EventSource не шлёт заголовки → авторизация одноразовым ticket в query);
 *   3) слушаем именованные события: payment.updated | payment.deleted | stats | event.created |
 *      task.created | error; `ping` (keep-alive) игнорируем;
 *   4) при обрыве — экспоненциальный реконнект, каждый раз берём НОВЫЙ тикет (старый одноразовый).
 *
 * Сверено с backend: payments.controller.ts (streamTicket/stream, ?ticket=),
 * sse-ticket.service.ts ({ ticket, expiresIn }), realtime.service.ts (имена событий, ping 25с).
 */

import { useEffect, useRef } from 'react';
import { apiClient, apiUrl, getAccessToken } from '../lib/apiClient';
import type {
  SseError,
  SseEvent,
  SseEventCreated,
  SsePaymentDeleted,
  SsePaymentUpdated,
  SseStats,
  SseTaskCreated,
  StreamTicketResponse,
} from '../lib/types';

/** Именованные доменные события, которые слушаем (без ping). */
const DOMAIN_EVENTS = [
  'payment.updated',
  'payment.deleted',
  'stats',
  'event.created',
  'task.created',
  'error',
] as const;

export interface UseSseOptions {
  /** Включить подписку (например, только когда вкладка активна). */
  enabled?: boolean;
  /** Обработчик разобранного доменного события. */
  onEvent: (event: SseEvent) => void;
}

/** Разбор data строки события в типизированный SseEvent. */
function parseEvent(name: string, raw: string): SseEvent | null {
  let data: unknown;
  try {
    data = raw ? JSON.parse(raw) : {};
  } catch {
    return null;
  }
  switch (name) {
    case 'payment.updated':
      return { name, data: data as SsePaymentUpdated };
    case 'payment.deleted':
      return { name, data: data as SsePaymentDeleted };
    case 'stats':
      return { name, data: data as SseStats };
    case 'event.created':
      return { name, data: data as SseEventCreated };
    case 'task.created':
      return { name, data: data as SseTaskCreated };
    case 'error':
      return { name, data: data as SseError };
    default:
      return null;
  }
}

/** Базовая и максимальная задержки реконнекта. */
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

export function useSSE(dealId: string | null, options: UseSseOptions): void {
  const { enabled = true, onEvent } = options;
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled || !dealId) return;

    let source: EventSource | null = null;
    let disposed = false;
    let reconnectAttempt = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const cleanupSource = (): void => {
      if (source) {
        for (const name of DOMAIN_EVENTS) source.removeEventListener(name, handleNamed as EventListener);
        source.close();
        source = null;
      }
    };

    const handleNamed = (ev: MessageEvent): void => {
      const parsed = parseEvent(ev.type, ev.data);
      if (parsed) onEventRef.current(parsed);
    };

    const scheduleReconnect = (): void => {
      if (disposed) return;
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** reconnectAttempt, RECONNECT_MAX_MS);
      reconnectAttempt += 1;
      reconnectTimer = setTimeout(() => void connect(), delay);
    };

    const connect = async (): Promise<void> => {
      if (disposed) return;
      cleanupSource();

      // Без access-токена тикет не получить — подождём следующего цикла.
      if (!getAccessToken()) {
        scheduleReconnect();
        return;
      }

      let ticket: string;
      try {
        const res = await apiClient.post<StreamTicketResponse>(`/api/deals/${dealId}/stream-ticket`);
        ticket = res.ticket;
      } catch {
        scheduleReconnect();
        return;
      }
      if (disposed) return;

      const url = `${apiUrl(`/api/deals/${dealId}/stream`)}?ticket=${encodeURIComponent(ticket)}`;
      // withCredentials — на случай, если раздача SSE на нашем домене и понадобится cookie;
      // авторизация всё равно по ticket, cookie не обязательна.
      const es = new EventSource(url, { withCredentials: true });
      source = es;

      es.onopen = () => {
        reconnectAttempt = 0;
      };

      for (const name of DOMAIN_EVENTS) {
        es.addEventListener(name, handleNamed as EventListener);
      }

      es.onerror = () => {
        // EventSource сам пытается реконнектиться, но наш тикет одноразовый —
        // поэтому закрываем и переустанавливаем с новым тикетом.
        cleanupSource();
        scheduleReconnect();
      };
    };

    void connect();

    return () => {
      disposed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      cleanupSource();
    };
  }, [dealId, enabled]);
}
